// apps/web/src/lib/vendas/relatorio.ts — Fase 12, Plano 17 (PV-08, D-33..D-36, T-12-82).
// Funções puras do relatório de acompanhamento ao cliente: rótulos de estado e regra do polling.
import type { EstadoExpedicaoPv, EstadoRelatorioPv, FormatoRelatorioPv, SecaoRelatorioPv } from '@pieltec/contracts';

type EvidenciaExpedicao = Readonly<{
  estado: EstadoExpedicaoPv;
  numeroNfOmieComprovado: string | null;
}>;

/**
 * A cópia deixa explícito que a expedição ainda não existe para o legado e
 * evita tratar a ausência de conciliação fiscal como número de NF ou zero.
 */
export function rotuloEstadoExpedicaoRelatorio(expedicao: EvidenciaExpedicao | null): string {
  if (!expedicao) return 'Não disponível';
  switch (expedicao.estado) {
    case 'A_EXPEDIR':
      return 'A expedir';
    case 'PARCIAL':
      return 'Parcial';
    case 'CONCLUIDA':
      return 'Concluída';
  }
}

/** Só existe NF exibível quando a projeção recebeu evidência de conciliação externa. */
export function rotuloNfOmieRelatorio(expedicao: EvidenciaExpedicao | null): string {
  return expedicao?.numeroNfOmieComprovado ?? 'Não comprovada';
}

/** Rótulo do estado persistido do relatório (UI-SPEC § Relatório de acompanhamento, D-36). */
export function rotuloEstadoRelatorio(estado: EstadoRelatorioPv): string {
  switch (estado) {
    case 'PENDENTE':
    case 'GERANDO':
      return 'Gerando relatório…';
    case 'PRONTO':
      return 'Relatório pronto.';
    case 'FALHA':
      return 'Não foi possível gerar o relatório. Tente novamente.';
    case 'EXPIRADO':
      return 'Expirado';
  }
}

/** O painel só consulta o servidor enquanto houver relatório em geração (T-12-82). */
export function deveContinuarConsultando(estados: readonly EstadoRelatorioPv[]): boolean {
  return estados.some((estado) => estado === 'PENDENTE' || estado === 'GERANDO');
}

// Polling do painel de relatórios (WR-D1-06): teto, backoff e limite de falhas seguidas.
export const INTERVALO_CONSULTA_MS = 3000;
export const INTERVALO_MAXIMO_CONSULTA_MS = 15000;
/** Depois disto sem terminar, o painel para de consultar e mostra "está demorando" com botão Atualizar. */
export const TETO_CONSULTA_MS = 5 * 60 * 1000;
/** Falhas seguidas (rede, 404, 5xx) até o painel desistir e mostrar a falha. */
export const FALHAS_SEGUIDAS_MAXIMAS = 5;

/** Espera até a próxima consulta: 3 s sem falhas, dobrando a cada falha seguida até 15 s. */
export function proximoIntervaloConsulta(falhasSeguidas: number): number {
  const falhas = Math.max(0, Math.floor(falhasSeguidas));
  return Math.min(INTERVALO_CONSULTA_MS * 2 ** falhas, INTERVALO_MAXIMO_CONSULTA_MS);
}

/** True quando a janela de consulta (a partir de `inicioMs`) passou do teto. */
export function consultaExpirou(inicioMs: number, agoraMs: number): boolean {
  return agoraMs - inicioMs >= TETO_CONSULTA_MS;
}

export const MENSAGEM_GERACAO_DEMORANDO = 'A geração está demorando. Atualize mais tarde.';
export const MENSAGEM_FALHA_ACOMPANHAR = 'Não foi possível acompanhar a geração do relatório. Atualize para tentar de novo.';

export interface EstadoFormularioRelatorio {
  readonly clienteId: string;
  readonly secoes: readonly SecaoRelatorioPv[];
  readonly formatos: readonly FormatoRelatorioPv[];
  readonly incluirConcluidos?: boolean;
  /** AAAA-MM-DD (valor de `<input type="date">`). */
  readonly periodoDe?: string | null;
  readonly periodoAte?: string | null;
}

/** "Gerar relatório" só habilita com cliente, ao menos uma seção e um formato (e De/Até quando incluir concluídos). */
export function podeGerarRelatorio(estado: EstadoFormularioRelatorio): boolean {
  if (!estado.clienteId) return false;
  if (estado.secoes.length === 0 || estado.formatos.length === 0) return false;
  if (!estado.incluirConcluidos) return true;
  const de = estado.periodoDe ?? '';
  const ate = estado.periodoAte ?? '';
  if (!de || !ate) return false;
  // Datas ISO (AAAA-MM-DD) comparam corretamente como texto.
  return de <= ate;
}

/** Mensagem do 409 SEM_PV_ABERTO: usa a do servidor (já traz o texto do UI-SPEC); o texto local é só plano B. */
export function mensagemSemPvAberto(mensagemServidor: unknown, clienteNome: string): string {
  if (typeof mensagemServidor === 'string' && mensagemServidor.trim() !== '') return mensagemServidor;
  return `${clienteNome} não tem PV em aberto. Marque "Incluir concluídos no período" para gerar mesmo assim.`;
}
